import React, { useState } from "react";
import history from "../../utils/history";
import urls from "../../global/constants/UrlConstants";
import {
  useActiveIvrCalls,
  clearActiveIvrCalls,
} from "../../utils/useActiveIvrCalls";
import { getCallParams, makeCall } from "../../utils/service";

const statusLabel: Record<string, string> = {
  initiated: "Connecting",
  dialing: "Dialing",
  ringing: "Ringing",
  answered: "On call",
};

/**
 * Top-right header chip: live IVR duration while a call is in progress.
 * Height matches other header controls (40px) for vertical alignment.
 */
export default function OngoingCallChip() {
  const { primary, hasActiveCall, calls } = useActiveIvrCalls();
  const [ending, setEnding] = useState(false);

  if (!hasActiveCall || !primary) return null;

  const label = statusLabel[primary.status] || primary.status;
  const who =
    primary.leadName ||
    primary.customerNumber ||
    (calls.length > 1 ? `${calls.length} calls` : "Customer");

  const endCall = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (ending) return;
    setEnding(true);
    const payload = {
      activeCallId: primary._id,
      uniqueCallId: primary.uniqueCallId,
      referenceId: primary.referenceId,
      endAllForAgent: true,
    };
    // Hide chip immediately; don't refetch until End API completes
    clearActiveIvrCalls({ refetch: false });
    try {
      const params = await getCallParams("POST", payload);
      await makeCall(urls.ACTIVECALLSEND, params);
    } catch (_) {
      /* ignore */
    } finally {
      await clearActiveIvrCalls({ refetch: true });
      setEnding(false);
    }
  };

  return (
    <div className="inline-flex h-10 max-w-[min(360px,42vw)] items-center gap-2 rounded-xl border border-[#E8D0D8] bg-[#FFF7F9] pl-3 pr-1.5">
      <button
        type="button"
        onClick={() => history.push(urls.teamViewPath)}
        title="View live IVR on Team"
        className="inline-flex h-full min-w-0 flex-1 items-center gap-2.5 text-left"
      >
        <span className="relative flex h-2 w-2 shrink-0" aria-hidden>
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#841C39] opacity-50" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-[#841C39]" />
        </span>
        <span className="min-w-0 leading-none">
          <span className="block truncate text-[11px] font-semibold tracking-wide text-[#841C39]">
            {label}
            <span className="font-normal text-[#9F6B7A]"> · </span>
            <span className="font-medium text-[#4B5563]">{who}</span>
          </span>
          <span className="mt-0.5 block font-mono text-[13px] font-semibold tabular-nums text-[#111827]">
            {primary.durationFormatted || "0:00"}
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={endCall}
        disabled={ending}
        className="inline-flex h-7 shrink-0 items-center justify-center rounded-lg border border-[#E8D0D8] bg-white px-2.5 text-[11px] font-semibold text-[#841C39] transition hover:bg-[#FCEEF3] disabled:opacity-50"
        title="Mark call ended"
      >
        {ending ? "…" : "End"}
      </button>
    </div>
  );
}
